/*
 * Copyright (C) Sitevision AB 2002-2026, all rights reserved
 */

const STATE_CHANGE_EVENT = 'SVWebAppCaptchaStateChanged';

const dispatchStateChange = (widget, state) => {
  if (widget.getAttribute('data-sv-captcha-state') === state) {
    return;
  }

  widget.setAttribute('data-sv-captcha-state', state);

  let event;
  if (typeof window.CustomEvent === 'function') {
    event = new CustomEvent(STATE_CHANGE_EVENT, { bubbles: true });
  } else {
    event = document.createEvent('CustomEvent');
    event.initCustomEvent(STATE_CHANGE_EVENT, true, false, null);
  }
  widget.dispatchEvent(event);
};

function findCaptchaElements(root, selector) {
  const elements = root.querySelectorAll(selector);

  if (root.matches && root.matches(selector)) {
    return [root, ...elements];
  }

  return elements;
}

export function initializeFriendlyCaptcha(root = document) {
  const friendlyCaptchaV1Elements = findCaptchaElements(root, '.frc-captcha:not([data-friendly-captcha-v2])');
  const friendlyCaptchaV2Elements = findCaptchaElements(root, '.frc-captcha[data-friendly-captcha-v2]');

  if (friendlyCaptchaV1Elements.length) {
    import(/* webpackChunkName: "friendly-c" */ 'friendly-challenge').then(({ WidgetInstance }) => {
      window.friendlyChallenge = window.friendlyChallenge || {};
      window.friendlyChallenge.WidgetInstance = WidgetInstance;
      for (let i = 0; i < friendlyCaptchaV1Elements.length; i++) {
        const element = friendlyCaptchaV1Elements[i];
        if (!element.dataset.attached) {
          // Only WebApps expose SDK state; regular portlets retain their defaults.
          const options = element.getAttribute('data-sv-captcha-provider') === 'friendly' ? {
            readyCallback: () => dispatchStateChange(element, 'pending'),
            startedCallback: () => dispatchStateChange(element, 'pending'),
            doneCallback: () => dispatchStateChange(element, 'verified'),
            errorCallback: () => dispatchStateChange(element, 'error'),
          } : undefined;
          window.friendlyChallenge.autoWidget = new WidgetInstance(element, options);
          element.dataset.attached = '1';
        }
      }
    });
  }

  if (friendlyCaptchaV2Elements.length) {
    import(/* webpackChunkName: "friendly-c-v2" */ '@friendlycaptcha/sdk').then(({ FriendlyCaptchaSDK }) => {
      const elements = [];

      for (let i = 0; i < friendlyCaptchaV2Elements.length; i++) {
        const element = friendlyCaptchaV2Elements[i];
        if (!element.frcWidget) {
          elements[elements.length] = element;
        }
      }

      if (elements.length) {
        new FriendlyCaptchaSDK().attach(elements);
      }
    });
  }
}

((doc) => {
  const getNormalizedState = (state) => {
    if (state === 'completed') {
      return 'verified';
    }

    if (state === 'expired') {
      return 'expired';
    }

    if (state === 'error' || state === 'destroyed') {
      return 'error';
    }

    return 'pending';
  };

  doc.addEventListener('frc:widget.statechange', (event) => {
    const widget = event.target;

    if (
      !widget ||
      widget.getAttribute('data-sv-captcha-provider') !== 'friendly'
    ) {
      return;
    }

    dispatchStateChange(widget, getNormalizedState(event.detail.state));
  });
  doc.addEventListener('SVInitCaptchaTrigger', () => initializeFriendlyCaptcha());

  if (doc.readyState !== 'loading') {
    initializeFriendlyCaptcha();
  } else {
    doc.addEventListener('DOMContentLoaded', () => initializeFriendlyCaptcha());
  }
})(document);
